import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  COBALT_PROVIDER_POLL_INTERVAL_MS,
  COBALT_PROVIDER_REFRESH_TIMEOUT_MS,
} from '@/config/constants'
import { requireAuthToken } from '@/lib/api'
import { cobaltConnectionKeys } from '@/lib/queryKeys'
import {
  fetchCobaltProviderRefresh,
  refreshCobaltProviders,
} from './cobaltProvidersApi'

function storedRefresh(
  key: string
): { jobId: string; startedAt: number } | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null')
    return value &&
      typeof value.jobId === 'string' &&
      typeof value.startedAt === 'number'
      ? value
      : null
  } catch {
    return null
  }
}

export function useCobaltProviderDiscovery({
  orgId,
  getToken,
  autoDiscover,
  onDiscoveryStarted,
}: {
  orgId: string
  getToken: () => Promise<string | null>
  autoDiscover?: boolean
  onDiscoveryStarted?: () => void
}) {
  const queryClient = useQueryClient()
  const storageKey = `cobalt-provider-refresh:${orgId}`
  const [refresh, setRefresh] = useState(() => storedRefresh(storageKey))
  const [message, setMessage] = useState('')
  const [timedOut, setTimedOut] = useState(false)
  const autoStarted = useRef(false)
  const discovery = useQuery({
    queryKey: cobaltConnectionKeys.providerRefresh(orgId, refresh?.jobId),
    queryFn: async ({ signal }) =>
      fetchCobaltProviderRefresh(
        await requireAuthToken(getToken),
        refresh!.jobId,
        signal
      ),
    enabled: !!refresh && !timedOut,
    refetchInterval: (query) =>
      !query.state.error &&
      (query.state.data?.status === 'pending' || !query.state.data)
        ? COBALT_PROVIDER_POLL_INTERVAL_MS
        : false,
    retry: false,
  })

  const rememberRefresh = useCallback(
    (value: typeof refresh) => {
      setRefresh(value)
      try {
        if (value) sessionStorage.setItem(storageKey, JSON.stringify(value))
        else sessionStorage.removeItem(storageKey)
      } catch {
        /* Discovery still works when browser storage is disabled. */
      }
    },
    [storageKey]
  )

  const start = useMutation({
    mutationFn: async () =>
      refreshCobaltProviders(await requireAuthToken(getToken)),
    onSuccess: (result) => {
      setMessage('')
      setTimedOut(false)
      rememberRefresh({ jobId: result.job_id, startedAt: Date.now() })
      // Cobalt can join the same running job; retry its status even if the
      // previous poll failed. Keep the old job if starting discovery fails.
      void queryClient.invalidateQueries({
        queryKey: cobaltConnectionKeys.providerRefresh(orgId, result.job_id),
        exact: true,
      })
    },
    onError: () =>
      setMessage('Provider discovery could not be started. Try again.'),
  })
  const { mutate: startDiscovery } = start

  useEffect(() => {
    if (!autoDiscover || autoStarted.current) return
    let cancelled = false
    // Wait for mount effects to settle so StrictMode's probe cannot detach
    // the mutation observer after it has already submitted discovery.
    queueMicrotask(() => {
      if (cancelled || autoStarted.current) return
      autoStarted.current = true
      onDiscoveryStarted?.()
      if (!refresh) startDiscovery()
    })
    return () => {
      cancelled = true
    }
  }, [autoDiscover, onDiscoveryStarted, refresh, startDiscovery])

  useEffect(() => {
    if (!refresh) return
    const timer = setTimeout(
      () => setTimedOut(true),
      Math.max(
        0,
        refresh.startedAt + COBALT_PROVIDER_REFRESH_TIMEOUT_MS - Date.now()
      )
    )
    return () => clearTimeout(timer)
  }, [refresh])

  // Observe cached completions too, so restoring a retained job settles the
  // UI even when no new poll runs. Keep this out of the shared query function.
  useEffect(() => {
    const status = discovery.data?.status
    if (!refresh || !status || status === 'pending') return
    if (status === 'succeeded') {
      void queryClient.invalidateQueries({
        queryKey: cobaltConnectionKeys.providers(orgId),
        exact: true,
      })
      setMessage('Provider discovery complete. Select the providers to sync.')
    } else
      setMessage(
        'Provider discovery failed. Try refreshing the directory again.'
      )
    rememberRefresh(null)
  }, [discovery.data, refresh, orgId, queryClient, rememberRefresh])

  const busy = start.isPending || (!!refresh && !timedOut && !discovery.isError)
  return {
    busy,
    hasRetainedJob: !!refresh,
    needsCheck: !!refresh && (timedOut || discovery.isError),
    message,
    startDiscovery: () => start.mutate(),
    checkAgain: () => {
      if (!refresh) return
      rememberRefresh({ ...refresh, startedAt: Date.now() })
      setTimedOut(false)
      void discovery.refetch()
    },
  }
}
